import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { Loader2, ExternalLink, Tag } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface AffiliateProgram {
  id: string;
  retailer_name: string;
  base_url: string;
  tracking_param_name: string;
  tracking_code: string | null;
  notes: string | null;
  is_active: boolean;
}

interface ProductCount {
  retailer: string;
  count: number;
}

export const AffiliateLinksAdmin = () => {
  const [programs, setPrograms] = useState<AffiliateProgram[]>([]);
  const [productCounts, setProductCounts] = useState<ProductCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    loadPrograms();
    loadProductCounts();
  }, []);

  const loadPrograms = async () => {
    try {
      const { data, error } = await supabase
        .from('affiliate_programs')
        .select('*')
        .order('retailer_name');

      if (error) throw error;
      setPrograms(data || []);
    } catch (error) {
      console.error('Error loading affiliate programs:', error);
      toast({
        title: "Error",
        description: "Failed to load affiliate programs",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadProductCounts = async () => {
    try {
      const { data: products, error } = await supabase
        .from('symptom_products')
        .select('link')
        .not('link', 'is', null);

      if (error) throw error;

      // Count products by retailer based on URL
      const counts: { [key: string]: number } = {};
      products?.forEach(product => {
        if (product.link) {
          const url = new URL(product.link);
          const hostname = url.hostname.replace('www.', '');
          
          let retailer = 'Other';
          if (hostname.includes('amazon.com')) retailer = 'Amazon';
          else if (hostname.includes('target.com')) retailer = 'Target';
          else if (hostname.includes('chemistwarehouse.com')) retailer = 'Chemist Warehouse';
          else if (hostname.includes('priceline.com')) retailer = 'Priceline';
          else if (hostname.includes('iherb.com')) retailer = 'iHerb';

          counts[retailer] = (counts[retailer] || 0) + 1;
        }
      });

      setProductCounts(
        Object.entries(counts).map(([retailer, count]) => ({ retailer, count }))
      );
    } catch (error) {
      console.error('Error loading product counts:', error);
    }
  };

  const updateProgram = async (id: string, updates: Partial<AffiliateProgram>) => {
    setUpdating(id);
    try {
      const { error } = await supabase
        .from('affiliate_programs')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      setPrograms(programs.map(p => p.id === id ? { ...p, ...updates } : p));
      
      toast({
        title: "Success",
        description: "Affiliate program updated successfully"
      });
    } catch (error) {
      console.error('Error updating program:', error);
      toast({
        title: "Error",
        description: "Failed to update affiliate program",
        variant: "destructive"
      });
    } finally {
      setUpdating(null);
    }
  };

  const applyTrackingToProducts = async (program: AffiliateProgram) => {
    if (!program.tracking_code) {
      toast({
        title: "No Tracking Code",
        description: "Please add a tracking code first",
        variant: "destructive"
      });
      return;
    }

    setUpdating(program.id);
    try {
      // Get all products that match this retailer
      const { data: products, error: fetchError } = await supabase
        .from('symptom_products')
        .select('id, link')
        .not('link', 'is', null);

      if (fetchError) throw fetchError;

      const matchingProducts = products?.filter(product => {
        if (!product.link) return false;
        try {
          const url = new URL(product.link);
          return url.hostname.includes(program.base_url.replace('https://', '').replace('www.', ''));
        } catch {
          return false;
        }
      }) || [];

      // Update each product with tracking code
      let updatedCount = 0;
      for (const product of matchingProducts) {
        try {
          const url = new URL(product.link!);
          url.searchParams.set(program.tracking_param_name, program.tracking_code!);
          
          const { error: updateError } = await supabase
            .from('symptom_products')
            .update({ link: url.toString() })
            .eq('id', product.id);

          if (!updateError) updatedCount++;
        } catch (error) {
          console.error('Error updating product:', product.id, error);
        }
      }

      toast({
        title: "Success",
        description: `Updated ${updatedCount} product links with tracking code`
      });

      loadProductCounts();
    } catch (error) {
      console.error('Error applying tracking codes:', error);
      toast({
        title: "Error",
        description: "Failed to update product links",
        variant: "destructive"
      });
    } finally {
      setUpdating(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Affiliate Programs Manager</CardTitle>
          <CardDescription>
            Manage tracking codes for affiliate programs and bulk update product links
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Product Distribution */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {productCounts.map(({ retailer, count }) => (
                <Card key={retailer}>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">{count}</div>
                    <p className="text-sm text-muted-foreground">{retailer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Affiliate Programs */}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Retailer</TableHead>
                  <TableHead>Tracking Code</TableHead>
                  <TableHead>Parameter</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {programs.map((program) => (
                  <TableRow key={program.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{program.retailer_name}</div>
                        <div className="text-sm text-muted-foreground flex items-center gap-1">
                          <ExternalLink className="h-3 w-3" />
                          {program.base_url}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Input
                        value={program.tracking_code || ''}
                        onChange={(e) => {
                          const newPrograms = programs.map(p =>
                            p.id === program.id ? { ...p, tracking_code: e.target.value } : p
                          );
                          setPrograms(newPrograms);
                        }}
                        placeholder="Enter tracking code"
                        className="max-w-xs"
                      />
                    </TableCell>
                    <TableCell>
                      <code className="text-sm bg-muted px-2 py-1 rounded">
                        {program.tracking_param_name}
                      </code>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={program.is_active}
                        onCheckedChange={(checked) =>
                          updateProgram(program.id, { is_active: checked })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateProgram(program.id, { tracking_code: program.tracking_code })}
                          disabled={updating === program.id}
                        >
                          {updating === program.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            'Save'
                          )}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => applyTrackingToProducts(program)}
                          disabled={updating === program.id || !program.tracking_code}
                        >
                          {updating === program.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <Tag className="h-4 w-4 mr-1" />
                              Apply to Products
                            </>
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
